import { useRef } from "react";
import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Underline as UnderlineIcon,
  Palette,
  Asterisk,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { BubbleMenuButton } from "./bubble-menu-button";
import { LinkEditPopover } from "./link-edit-popover";

interface EditorBubbleMenuProps {
  editor: Editor;
  /** 각주 마커 인서트 핸들러. handbook editor 에서만 prop 으로 내려옴. */
  onInsertFootnote?: () => void;
}

export function EditorBubbleMenu({
  editor,
  onInsertFootnote,
}: EditorBubbleMenuProps) {
  // hidden input — 클릭 시 OS color picker 열어 hex 값 받음.
  const colorInputRef = useRef<HTMLInputElement>(null);

  const currentColor =
    (editor.getAttributes("textStyle").color as string | undefined) ?? "#000000";

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 150 }}
      className="flex items-center gap-0.5 rounded-md border bg-background p-1 shadow-md"
    >
      <BubbleMenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        aria-label="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      <BubbleMenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        aria-label="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      <BubbleMenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive("underline")}
        aria-label="Underline"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      <BubbleMenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
        aria-label="Strikethrough"
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      <BubbleMenuButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive("code")}
        aria-label="Code"
      >
        <Code className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      {/* 컬러 picker — 버튼 클릭 → hidden color input click. 선택된 hex 가 Color extension 으로
          전달되어 백엔드 sanitize-html 의 color: #hex 정규식 통과. */}
      <BubbleMenuButton
        onClick={() => colorInputRef.current?.click()}
        active={Boolean(editor.getAttributes("textStyle").color)}
        aria-label="Font color"
        style={{
          borderBottom: editor.getAttributes("textStyle").color
            ? `2px solid ${currentColor}`
            : undefined,
        }}
      >
        <Palette className="h-3.5 w-3.5" />
        <input
          ref={colorInputRef}
          type="color"
          value={currentColor}
          onChange={(e) => {
            editor.chain().focus().setColor(e.target.value).run();
          }}
          className="absolute h-0 w-0 opacity-0"
          tabIndex={-1}
          aria-hidden="true"
        />
      </BubbleMenuButton>

      {editor.getAttributes("textStyle").color && (
        <BubbleMenuButton
          onClick={() => editor.chain().focus().unsetColor().run()}
          aria-label="Clear color"
        >
          <span className="text-[10px] font-bold">×</span>
        </BubbleMenuButton>
      )}

      <div className="mx-0.5 h-4 w-px bg-border" />

      {/* 본문 정렬 — 좌·중·우. 같은 정렬 재클릭 시 해제(좌로 복귀). */}
      <BubbleMenuButton
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        active={editor.isActive({ textAlign: "left" })}
        aria-label="왼쪽 정렬"
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </BubbleMenuButton>
      <BubbleMenuButton
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        active={editor.isActive({ textAlign: "center" })}
        aria-label="가운데 정렬"
      >
        <AlignCenter className="h-3.5 w-3.5" />
      </BubbleMenuButton>
      <BubbleMenuButton
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        active={editor.isActive({ textAlign: "right" })}
        aria-label="오른쪽 정렬"
      >
        <AlignRight className="h-3.5 w-3.5" />
      </BubbleMenuButton>

      <div className="mx-0.5 h-4 w-px bg-border" />

      <LinkEditPopover editor={editor} />

      {onInsertFootnote && (
        <>
          <div className="mx-0.5 h-4 w-px bg-border" />
          <BubbleMenuButton
            onClick={onInsertFootnote}
            aria-label="Insert footnote"
          >
            <Asterisk className="h-3.5 w-3.5" />
          </BubbleMenuButton>
        </>
      )}
    </BubbleMenu>
  );
}
